import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Calendar, MapPin, Users, Ticket, Loader2, CreditCard, AlertCircle, Minus, Plus, Percent } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import AppImage from '../components/AppImage';
import CreateEventModal from '../components/CreateEventModal';
import SiteSections from '../components/SiteSections';

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [qtyById, setQtyById] = useState({});
  const [namesById, setNamesById] = useState({});   // { eid: ["Alice", "", ...] }
  const [namesOpenById, setNamesOpenById] = useState({});
  const [resaleByEvent, setResaleByEvent] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const canHost = !!(user && (user.isAdmin || user.role === 'admin' || user.isBusinessOwner));

  useEffect(() => {
    evtApi.list().then(list => {
      setItems(list);
      // Best-effort resale-listings pre-fetch so cards show "N tickets on resale"
      list.forEach(async e => {
        if (Number(e.price) > 0) {
          try {
            const r = await evtApi.resaleListings(e.id);
            if (r?.length) setResaleByEvent(p => ({ ...p, [e.id]: r }));
          } catch { /* ignore auth errors when not signed in */ }
        }
      });
    }).finally(() => setLoading(false));
  }, []);

  const rsvps = new Set(user?.rsvpEvents || []);
  const qtyOf = (id) => Math.max(1, Math.min(10, Number(qtyById[id] || 1)));
  const setQty = (id, v) => setQtyById(p => ({ ...p, [id]: Math.max(1, Math.min(10, Number(v) || 1)) }));

  const namesOf = (id) => namesById[id] || [];
  const setNameAt = (id, idx, v) => setNamesById(p => {
    const arr = [...(p[id] || [])];
    arr[idx] = v;
    return { ...p, [id]: arr };
  });
  const namesTrimmed = (id, q) => {
    const arr = (namesById[id] || []).slice(0, q);
    while (arr.length < q) arr.push('');
    return arr;
  };

  const reserve = async (e) => {
    if (rsvps.has(e.id) || busyId === e.id) return;
    setBusyId(e.id);
    const q = qtyOf(e.id);
    const names = q > 1 ? namesTrimmed(e.id, q) : null;
    try {
      if (e.price && Number(e.price) > 0) {
        const s = await evtApi.checkoutSession(e.id, window.location.origin, q, names);
        if (s?.waitlisted) {
          toast({ title: 'Added to waitlist', description: 'Sold out for now — we\'ll notify you the moment a spot opens.' });
          return;
        }
        if (!s?.url) throw new Error('Stripe returned no URL');
        window.location.href = s.url;
        return;
      }
      const r = await evtApi.reserve(e.id, q, names);
      if (r?.waitlisted) {
        toast({ title: 'Added to waitlist', description: 'Sold out for now — we\'ll notify you the moment a spot opens.' });
        return;
      }
      setItems(prev => prev.map(it => it.id === e.id ? (r.event || it) : it));
      await refresh();
      toast({
        title: q > 1 ? `${q} tickets reserved!` : 'Ticket reserved!',
        description: `${e.title} · email & SMS on the way`,
      });
      if (r.ticket?.ticketId) navigate(`/tickets/${r.ticket.ticketId}`);
    } catch (err) {
      toast({ title: 'Reservation failed', description: err?.response?.data?.detail || err?.message || 'Try again' });
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Event Calendar</h1>
          <p className="text-gray-400 text-sm">Sell tickets, RSVP, and earn points. Powered by PBB.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canHost && (
            <button type="button" onClick={() => setCreateOpen(true)} data-testid="create-event-btn"
              className="text-sm font-semibold px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white inline-flex items-center gap-1.5 shadow-lg shadow-green-500/20">
              <Plus size={14}/> Create event
            </button>
          )}
          <Link to="/my/tickets" data-testid="my-tickets-link"
            className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1">
            <Ticket size={12}/> My tickets
          </Link>
          <Link to="/resale" data-testid="resale-marketplace-link"
            className="text-xs px-3 py-1.5 rounded-lg border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10 inline-flex items-center gap-1">
            <Percent size={12}/> Resale market · 25% off
          </Link>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="events-empty-state">
          <Calendar className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">{canHost ? 'No events yet — be the first host' : 'Events launching soon'}</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">
            {canHost
              ? 'Publish your first event and members will start RSVPing right away.'
              : 'Be one of the first 100 members — register now and you\'ll be notified the moment the first community event goes live.'}
          </p>
          {canHost ? (
            <button type="button" onClick={() => setCreateOpen(true)} data-testid="events-empty-create-btn"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">
              <Plus size={14}/> Create your first event
            </button>
          ) : (
            <Link to="/signup" data-testid="events-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">Register Free</Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(e => {
            const done = rsvps.has(e.id);
            const paid = Number(e.price) > 0;
            const busy = busyId === e.id;
            const cap = Number(e.capacity || 0);
            const remaining = cap ? Math.max(0, cap - (e.attending || 0)) : null;
            const soldOut = cap > 0 && remaining <= 0;
            const q = qtyOf(e.id);
            const resales = resaleByEvent[e.id] || [];
            return (
              <div key={e.id} className="pbb-card overflow-hidden flex flex-col h-full">
                <AppImage src={e.image} alt={e.title} rounded="none" fallback={<Calendar size={28}/>} />
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-semibold pbb-card-title" title={e.title}>{e.title}</div>
                  <div className="text-xs text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar size={12}/> {e.date} • {e.time}</span>
                    <span className="inline-flex items-center gap-1"><MapPin size={12}/> {e.city}</span>
                  </div>
                  <ClampWithMore text={e.description} lines={3} className="text-xs text-gray-400 mt-2" testId={`event-desc-${e.id}`} />
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <div className="text-xs text-gray-500">Attending</div>
                      <div className="text-sm inline-flex items-center gap-1"><Users size={12} className="text-green-400"/> {e.attending || 0}{cap ? ` / ${cap}` : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Price</div>
                      <div className="font-semibold text-green-400">{paid ? `$${e.price}` : 'Free'}</div>
                    </div>
                  </div>
                  {soldOut && (
                    <div className="mt-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-2 py-1.5 text-[11px] text-yellow-200 inline-flex items-center gap-1" data-testid={`event-soldout-${e.id}`}>
                      <AlertCircle size={12}/> Sold out — join the waitlist below.
                    </div>
                  )}
                  {resales.length > 0 && (
                    <Link to={`/events/${e.id}`} data-testid={`event-resales-${e.id}`}
                      className="mt-2 text-[11px] text-yellow-300 hover:text-yellow-200 inline-flex items-center gap-1">
                      🎟️ {resales.length} resale ticket{resales.length > 1 ? 's' : ''} at 25% off
                    </Link>
                  )}
                  {!done && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="inline-flex items-center rounded-lg border border-[#2a2a2a] bg-black overflow-hidden">
                        <button type="button" onClick={() => setQty(e.id, q - 1)} data-testid={`qty-dec-${e.id}`}
                          className="w-8 h-9 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-40" disabled={q <= 1}>
                          <Minus size={12}/>
                        </button>
                        <div className="w-8 text-center text-sm font-semibold text-white" data-testid={`qty-value-${e.id}`}>{q}</div>
                        <button type="button" onClick={() => setQty(e.id, q + 1)} data-testid={`qty-inc-${e.id}`}
                          className="w-8 h-9 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-40" disabled={q >= 10}>
                          <Plus size={12}/>
                        </button>
                      </div>
                      <div className="text-[10px] text-gray-500">up to 10</div>
                      {q > 1 && (
                        <button type="button" onClick={() => setNamesOpenById(p => ({ ...p, [e.id]: !p[e.id] }))}
                          data-testid={`attendee-names-toggle-${e.id}`}
                          className="ml-auto text-[11px] px-2 py-1 rounded border border-[#2a2a2a] hover:border-green-500 text-gray-300 hover:text-white">
                          {namesOpenById[e.id] ? 'Hide names' : 'Add attendee names (optional)'}
                        </button>
                      )}
                    </div>
                  )}
                  {!done && q > 1 && namesOpenById[e.id] && (
                    <div className="mt-2 space-y-1.5" data-testid={`attendee-names-list-${e.id}`}>
                      <div className="text-[10px] text-gray-500">Leave blank to use your name. Optional.</div>
                      {Array.from({ length: q }).map((_, idx) => (
                        <input key={idx} type="text" value={namesOf(e.id)[idx] || ''}
                          onChange={(ev) => setNameAt(e.id, idx, ev.target.value)}
                          maxLength={60}
                          placeholder={idx === 0 ? `Attendee 1 (you) name` : `Attendee ${idx + 1} name`}
                          data-testid={`attendee-name-input-${e.id}-${idx}`}
                          className="w-full text-xs bg-black border border-[#2a2a2a] rounded-md px-2 py-1.5 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"/>
                      ))}
                    </div>
                  )}
                  <button onClick={() => reserve(e)} disabled={done || busy}
                    data-testid={`event-reserve-${e.id}`}
                    className={`mt-3 w-full px-3 py-2 rounded-lg text-sm inline-flex items-center justify-center gap-1 ${done ? 'bg-[#0d1f14] border border-[#153b23] text-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}>
                    {busy ? <Loader2 size={14} className="animate-spin"/> : (paid ? <CreditCard size={14}/> : <Ticket size={14}/>)}
                    {done ? 'Ticket Reserved' : busy ? (paid ? 'Redirecting to Stripe…' : 'Reserving…')
                      : soldOut ? 'Join waitlist'
                      : paid ? (q > 1 ? `Buy ${q} × $${e.price}` : `Buy Ticket $${e.price}`)
                      : (q > 1 ? `Reserve ${q} Free Tickets` : 'Reserve Free Ticket')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <CreateEventModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={async (ev) => {
          // Prepend so the just-published event is at the top instantly.
          setItems(prev => [ev, ...prev]);
          try { const list = await evtApi.list(); setItems(list); } catch {}
        }}/>
      <SiteSections pageKey="events" />
    </div>
  );
}
